import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import useSound from "use-sound";
import toast from "react-hot-toast";
import { Dialog } from "primereact/dialog";

import Menu from "./Menu/Menu";
import StandingCard from "./StandingCard/StandingCard";
import TopCard from "./TopCard/TopCard";
import Filter from "./Filter/Filter";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import BuySell from "../../../Screen/User/WatchlistUser/BuySell/BuySell";

const backend = await new Backend().init();

export default function Standing({ user_id }) {
  const dispatch = useDispatch();
  const windowSize = useSize();
  const [playError] = useSound(ErrorSoundFile);

  const [loading, setLoading] = useState(false);
  const [standingDelete, setStandingDelete] = useState(false);
  const [positions, setPositions] = useState([]);
  const [allPositions, setAllPositions] = useState([]);

  const [buysell, setBuySell] = useState(false);
  const [buyselltype, setBuySellType] = useState(0);

  /** ✅ Build common user data */
  const buildUserData = useCallback(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      user_id: user_id,
    }),
    [user_id]
  );

  /** ✅ Main loader */
  const loadStanding = useCallback(async () => {
    setLoading(true);
    try {
      let data = buildUserData();

      let res = await backend.standing(data);

      if (res.error === "False") {
        setPositions(res.standing);
        setAllPositions(res.standing);
        setStandingDelete(res.standing_delete);
        dispatch({ type: "CLEAR_PFMODAL" });
      } else {
        if (res.logout === 1) {
          window.LogOut?.();
        } else {
          playError();
          toast.error(res.message || "Failed to load data");
        }
      }
    } catch (err) {
      console.error("Standing Load Error:", err);
      playError();
      toast.error("Failed to load standing data.");
    } finally {
      setLoading(false);
    }
  }, [buildUserData, dispatch, playError]);

  /** 🔹 Initial Load */
  useEffect(() => {
    loadStanding();
  }, [loadStanding]);

  /** 🔹 Symbol Search */
  const handleSearch = useCallback(
    (text) => {
      if (text.length > 1) {
        setPositions(
          allPositions.filter((x) =>
            x.symbol.toLowerCase().includes(text.toLowerCase())
          )
        );
      } else {
        setPositions(allPositions);
      }
    },
    [allPositions]
  );

  /** 🔹 Manual Reload */
  const handleReload = useCallback(() => loadStanding(), [loadStanding]);

  /** 🔹 Close Modal + Reload */
  const closeModal = useCallback(() => {
    setBuySell(false);
    loadStanding(); // ✅ Reload with current filters
  }, [loadStanding]);

  /** 🔹 Open Modal */
  const open = (a, b) => {
    window.symbolData = a;
    setBuySell(true);
    setBuySellType(b);
  };

  /** ✅ Responsive Layout */
  return (
    <div
      style={{
        width: windowSize[0] - 100,
        height: windowSize[1] - 170,
      }}
    >
      <Filter search={handleSearch} records={positions.length} />

      <TopCard user_id={user_id} />
      <Menu standing_delete={standingDelete} />

      <div
        key={loading ? "loading" : "loaded"}
        style={{
          width: windowSize[0] - 100,
          height: windowSize[1] - 330,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 330} />
          </div>
        ) : positions.length > 0 ? (
          <div
            style={{
              width: windowSize[0] - 100,
              height: windowSize[1] - 330,
              overflowY: "auto",
            }}
          >
            {positions.map((item, index) => (
              <StandingCard
                key={item.id}
                item={item}
                index={index + 1}
                total={positions.length}
                reload={handleReload}
                standing_delete={standingDelete}
                open={open}
              />
            ))}
          </div>
        ) : (
          <NotFound
            height={windowSize[1] - 330}
            title="Nothing here"
            subtext="No Standing Found."
          />
        )}
      </div>

      {/* ✅ Modal Section */}
      <Dialog
        visible={buysell}
        closeOnEscape
        closeIcon
        onHide={closeModal} // ✅ Reload screen on close
        className="modal"
        id="modal"
        position="center"
        transitionOptions={false}
        focusOnShow={false}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: windowSize[0],
          height: windowSize[1] - 100,
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
          }}
        >
          <BuySell
            buysell={buysell}
            symbolData={window.symbolData}
            close={closeModal} // ✅ Also reload when closed from inside
            buyselltype={buyselltype}
          />
        </div>
      </Dialog>
    </div>
  );
}
